package fyi.moko.hotpl.ui

import androidx.compose.foundation.layout.Box
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.navigation.NavType
import androidx.navigation.compose.NavHost
import androidx.navigation.compose.composable
import androidx.navigation.compose.currentBackStackEntryAsState
import androidx.navigation.compose.rememberNavController
import androidx.navigation.navArgument
import fyi.moko.hotpl.domain.service.UsageEvents
import fyi.moko.hotpl.domain.service.UsageTracker
import fyi.moko.hotpl.ui.account.AccountScreen
import fyi.moko.hotpl.ui.changelog.ChangelogScreen
import fyi.moko.hotpl.ui.detail.DetailScreen
import fyi.moko.hotpl.ui.home.HomeScreen
import fyi.moko.hotpl.ui.photo.PhotoViewerScreen
import fyi.moko.hotpl.ui.placeform.PlaceFormScreen
import org.koin.compose.koinInject

private object Routes {
    const val HOME = "home"
    const val ADD = "add"
    const val DETAIL = "detail/{placeId}"
    const val EDIT = "edit/{placeId}"
    // 뷰어는 오버레이가 아니라 목적지다 — 시스템 백과 iOS 엣지 스와이프가 둘 다
    // "뷰어 닫기"가 되게 하려면 백스택에 올라와 있어야 한다.
    const val PHOTO = "photo/{placeId}?visitId={visitId}&fileName={fileName}"
    const val ACCOUNT = "account"
    const val CHANGELOG = "changelog"
    fun detail(id: String) = "detail/$id"
    fun edit(id: String) = "edit/$id"
    fun photo(placeId: String, fileName: String, visitId: String? = null) =
        "photo/$placeId?visitId=${visitId.orEmpty()}&fileName=$fileName"
}

@Composable
fun HotplApp() {
    val navController = rememberNavController()
    // Edge-swipe back is only meaningful off the start destination (HOME) and only
    // where the OS doesn't already provide it (iOS — see EdgeSwipeBackGesture).
    val currentEntry by navController.currentBackStackEntryAsState()
    val canGoBack = currentEntry?.destination?.route?.let { it != Routes.HOME } ?: false

    // Usage log (0-39): every destination change in one place. The route pattern, not the
    // resolved path — "detail/{placeId}" says which screen, and which place is nobody's business.
    // HOME is reported by HomeScreen itself, per tab.
    val usageTracker = koinInject<UsageTracker>()
    val currentRoute = currentEntry?.destination?.route
    LaunchedEffect(currentRoute) {
        val name = currentRoute?.substringBefore('/')?.substringBefore('?') ?: return@LaunchedEffect
        if (name != Routes.HOME) usageTracker.track(UsageEvents.SCREEN, mapOf("name" to name))
    }

    Box {
        NavHost(navController = navController, startDestination = Routes.HOME) {
            composable(Routes.HOME) {
                HomeScreen(
                    onAddPlace = { navController.navigate(Routes.ADD) },
                    onPlaceClick = { id -> navController.navigate(Routes.detail(id)) },
                    onOpenAccount = { navController.navigate(Routes.ACCOUNT) },
                    onOpenChangelog = { navController.navigate(Routes.CHANGELOG) },
                )
            }
            composable(Routes.ACCOUNT) {
                AccountScreen(onBack = { navController.popBackStack() })
            }
            composable(Routes.CHANGELOG) {
                ChangelogScreen(onBack = { navController.popBackStack() })
            }
            composable(Routes.ADD) {
                PlaceFormScreen(placeId = null, onClose = { navController.popBackStack() })
            }
            composable(
                route = Routes.DETAIL,
                arguments = listOf(navArgument("placeId") { type = NavType.StringType }),
            ) { entry ->
                val id = entry.savedStateHandle.get<String>("placeId") ?: return@composable
                DetailScreen(
                    placeId = id,
                    onBack = { navController.popBackStack() },
                    onEdit = { navController.navigate(Routes.edit(id)) },
                    onDeleted = { navController.popBackStack() },
                    onOpenPhoto = { fileName, visitId ->
                        navController.navigate(Routes.photo(id, fileName, visitId))
                    },
                )
            }
            composable(
                route = Routes.PHOTO,
                arguments = listOf(
                    navArgument("placeId") { type = NavType.StringType },
                    navArgument("visitId") { type = NavType.StringType; defaultValue = "" },
                    navArgument("fileName") { type = NavType.StringType; defaultValue = "" },
                ),
            ) { entry ->
                val id = entry.savedStateHandle.get<String>("placeId") ?: return@composable
                PhotoViewerScreen(
                    placeId = id,
                    visitId = entry.savedStateHandle.get<String>("visitId")?.takeIf { it.isNotBlank() },
                    fileName = entry.savedStateHandle.get<String>("fileName")?.takeIf { it.isNotBlank() },
                    onClose = { navController.popBackStack() },
                )
            }
            composable(
                route = Routes.EDIT,
                arguments = listOf(navArgument("placeId") { type = NavType.StringType }),
            ) { entry ->
                val id = entry.savedStateHandle.get<String>("placeId") ?: return@composable
                PlaceFormScreen(placeId = id, onClose = { navController.popBackStack() })
            }
        }

        EdgeSwipeBackGesture(
            onBack = { navController.popBackStack() },
            modifier = Modifier.align(Alignment.CenterStart),
            enabled = canGoBack && platformSupportsEdgeSwipeBack,
        )
    }
}